import createClient from "openapi-fetch"

import { getToken } from "@/api/getToken.ts"
import { handleSessionExpired } from "@/authSession.ts"
import { API_BASE } from "@/config.ts"

import type { paths } from "./schema.d.ts"

const API_ORIGIN = String(API_BASE).replace(/\/api\/?$/, "")

export const apiClient = createClient<paths>({
  baseUrl: API_ORIGIN,
  fetch: async (input) => {
    const token = await getToken()
    input.headers.set("Authorization", `Bearer ${token}`)

    const response = await fetch(input)

    if (response.status === 401) {
      handleSessionExpired()
    }

    return response
  },
})

// openapi-fetch never throws on 4XX/5XX responses on its own — it always
// resolves with { data, error, response } and expects the caller to check.
// Call this after every apiClient request; skipping it means a failed
// request silently looks like a success to callers.
export function assertOk(response: Response, error?: unknown): void
export function assertOk<T>(response: Response, error: unknown, data: T | undefined): T
export function assertOk<T>(response: Response, error?: unknown, data?: T): T | void {
  if (!response.ok) {
    const detail = typeof error === "string" ? error : JSON.stringify(error)
    throw new Error(`${response.status} ${response.statusText} from ${response.url}: ${detail}`)
  }

  // A documented success response without a body is still a contract failure
  // for callers that expect a value. openapi-fetch represents it as undefined.
  if (arguments.length === 3 && data === undefined) {
    throw new Error(
      `${response.status} ${response.statusText} from ${response.url}: expected a response body`,
    )
  }

  return data
}
